import React, { useEffect, useMemo, useState } from "react";
import { Bird, DollarSign, Loader2, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { Section, StatCard, Badge, Button, Input, Select, Field, EmptyState } from "@/components/premium/UI";

// ---- helpers ---------------------------------------------------------------
const fmt = (n, ccy) => {
  const v = Number(n || 0);
  const s = v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return ccy === "USD" ? `$${s}` : `R${s}`;
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const uuid = () => (crypto?.randomUUID?.() || `mc-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`);

const PRODUCT = { product_key: "poultry.broiler", product_label: "Live Broiler Chicken" };

// ---- main component --------------------------------------------------------
export default function MultiCurrencySale({ onCompleted }) {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_sales:write");

  const [batches, setBatches] = useState([]);
  const [prices, setPrices] = useState([]);          // active price rows
  const [rate, setRate] = useState(null);            // { rate, effective_from }
  const [cash, setCash] = useState(null);            // { actual, management_equivalent }
  const [loading, setLoading] = useState(true);

  const [batchId, setBatchId] = useState("");
  const [qty, setQty] = useState(1);
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState("ZAR");
  const [tenderMode, setTenderMode] = useState("ZAR"); // ZAR | USD | MIXED
  const [zarPaid, setZarPaid] = useState("");
  const [usdPaid, setUsdPaid] = useState("");
  const [zarChange, setZarChange] = useState("");           // change physically returned in ZAR
  const [usdChange, setUsdChange] = useState("");           // change physically returned in USD
  const [customer, setCustomer] = useState("");
  const [notes, setNotes] = useState("");
  const [clientRef, setClientRef] = useState(() => uuid());  // idempotency key, per-sale
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);         // last successful tx

  async function reload() {
    setLoading(true);
    try {
      const [b, p, er, cp] = await Promise.all([
        api.get("/poultry/batches").catch(() => ({ data: [] })),
        api.get("/sales/prices").catch(() => ({ data: [] })),
        api.get("/sales/exchange-rates").catch(() => ({ data: [] })),
        api.get("/sales/cash-position").catch(() => ({ data: null })),
      ]);
      const activeBatches = (b.data || []).filter(x => (x.status !== "closed") && Number(x.current_birds || 0) > 0);
      setBatches(activeBatches);
      setPrices((p.data || []).filter(r => r.active !== false));
      const usdZar = (er.data || []).find(r => r.from_currency === "USD" && r.to_currency === "ZAR" && r.active !== false);
      setRate(usdZar || null);
      setCash(cp.data || null);
    } catch { /* silent */ } finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  const priceFor = (ccy) => prices.find(p => p.product_key === PRODUCT.product_key && p.currency === ccy)?.unit_price;
  const unitPrice = priceFor(currency);
  const total = useMemo(() => round2(Number(qty || 0) * Number(unitPrice || 0)), [qty, unitPrice]);

  const rateUsdZar = rate?.rate ? Number(rate.rate) : null;

  // Auto-tender default when currency toggles: ZAR sale defaults to ZAR-only tender
  useEffect(() => {
    if (tenderMode !== "MIXED") setTenderMode(currency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  const zarPaidNum = Number(zarPaid || 0);
  const usdPaidNum = Number(usdPaid || 0);
  const zarChangeNum = Number(zarChange || 0);
  const usdChangeNum = Number(usdChange || 0);

  // Settlement equivalent (tx-currency) NET of change physically returned
  const settlementEquivalent = useMemo(() => {
    const usdInTx = (ccyIn, val) => {
      if (val <= 0) return 0;
      if (ccyIn === currency) return val;
      if (currency === "ZAR" && ccyIn === "USD") return rateUsdZar ? val * rateUsdZar : 0;
      if (currency === "USD" && ccyIn === "ZAR") return rateUsdZar ? val / rateUsdZar : 0;
      return 0;
    };
    const grossPay   = usdInTx("ZAR", zarPaidNum) + usdInTx("USD", usdPaidNum);
    const grossChg   = usdInTx("ZAR", zarChangeNum) + usdInTx("USD", usdChangeNum);
    return round2(grossPay - grossChg);
  }, [currency, zarPaidNum, usdPaidNum, zarChangeNum, usdChangeNum, rateUsdZar]);

  const balance = round2(total - settlementEquivalent);
  const change = balance < 0 ? Math.abs(balance) : 0;
  const outstanding = balance > 0 ? balance : 0;

  const selectedBatch = batches.find(b => b.id === batchId);
  const available = selectedBatch ? Number(selectedBatch.current_birds || 0) : 0;
  const qtyExceedsAvail = selectedBatch && Number(qty || 0) > available;

  // Build payments + change_returned arrays according to tenderMode
  const payments = useMemo(() => {
    const arr = [];
    if (tenderMode === "ZAR" || tenderMode === "MIXED") {
      if (zarPaidNum > 0) arr.push({ currency: "ZAR", amount: zarPaidNum, method: "cash" });
    }
    if (tenderMode === "USD" || tenderMode === "MIXED") {
      if (usdPaidNum > 0) arr.push({ currency: "USD", amount: usdPaidNum, method: "cash" });
    }
    return arr;
  }, [tenderMode, zarPaidNum, usdPaidNum]);

  const changeReturned = useMemo(() => {
    const arr = [];
    if (zarChangeNum > 0) arr.push({ currency: "ZAR", amount: zarChangeNum });
    if (usdChangeNum > 0) arr.push({ currency: "USD", amount: usdChangeNum });
    return arr;
  }, [zarChangeNum, usdChangeNum]);

  const canSubmit = canWrite && batchId && Number(qty) > 0 && !qtyExceedsAvail
                  && !!unitPrice && !!rateUsdZar && payments.length > 0 && !submitting;

  async function complete() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        sale_date: saleDate || new Date().toISOString().slice(0, 10),
        revenue_category: "Poultry Sale",
        product_key: PRODUCT.product_key,
        product_label: PRODUCT.product_label,
        source_module: "poultry360",
        batch_id: batchId,
        quantity: Number(qty),
        transaction_currency: currency,
        customer: customer.trim(),
        payments,
        change_returned: changeReturned,
        client_reference: clientRef,
        notes,
      };
      const { data } = await api.post("/sales/transactions", body);
      const wasIdempotent = !!data.idempotent_hit;
      setResult(data);
      toast.success(wasIdempotent
        ? "Sale already recorded (idempotent replay)"
        : `Sale ${data.invoice_number} recorded`);
      onCompleted?.(data);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = err?.response?.status;
      let msg = detail || "Sale could not be completed.";
      if (code === 409) msg = "Sale cannot be completed. The available bird quantity has changed. Please review the current batch quantity.";
      else if (code === 400 && /No active .* price/.test(String(detail))) msg = "No active selling price is configured for the selected currency. Please contact an administrator.";
      else if (code === 400 && /rate/.test(String(detail))) msg = "Mixed-currency payment cannot be processed because an active exchange rate is unavailable.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function resetForNext() {
    setResult(null);
    setBatchId("");
    setQty(1);
    setCurrency("ZAR");
    setTenderMode("ZAR");
    setZarPaid(""); setUsdPaid("");
    setZarChange(""); setUsdChange("");
    setCustomer(""); setNotes("");
    setClientRef(uuid());                              // fresh idempotency key for next sale
  }

  // ---------------------------------------------------------- render ---------
  if (loading) {
    return <Section title="New multi-currency sale"><div className="animate-pulse h-40 rounded-xl bg-muted/40" /></Section>;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-testid="mc-sale-panel">
      {/* -------- Left: cash position + rate reference -------- */}
      <div className="space-y-6 xl:col-span-1">
        <Section title="Cash position" description="Actual currency held — never mixed">
          <div className="grid grid-cols-2 gap-3" data-testid="mc-cash-position">
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Actual ZAR</div>
              <div className="text-2xl font-heading font-bold tracking-tight" data-testid="mc-cash-zar">{fmt(cash?.actual?.ZAR, "ZAR")}</div>
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Actual USD</div>
              <div className="text-2xl font-heading font-bold tracking-tight" data-testid="mc-cash-usd">{fmt(cash?.actual?.USD, "USD")}</div>
            </div>
          </div>
          {cash?.management_equivalent?.value != null && (
            <div className="mt-3 text-xs text-muted-foreground border-t border-border pt-3">
              <span className="font-medium">Management equivalent (ZAR):</span> <span data-testid="mc-cash-mgmt">{fmt(cash.management_equivalent.value, "ZAR")}</span>
              <div className="mt-1 italic opacity-80">Reporting value only — not physical ZAR held.</div>
            </div>
          )}
        </Section>
        <Section title="Operational reference" description="Read-only">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">ZAR price / bird</span>
              <span className="font-semibold" data-testid="mc-price-zar">{priceFor("ZAR") ? fmt(priceFor("ZAR"), "ZAR") : "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">USD price / bird</span>
              <span className="font-semibold" data-testid="mc-price-usd">{priceFor("USD") ? fmt(priceFor("USD"), "USD") : "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Exchange rate</span>
              <span className="font-semibold" data-testid="mc-rate">USD 1 = R{rateUsdZar ? rateUsdZar.toFixed(2) : "—"}</span>
            </div>
          </div>
        </Section>
      </div>

      {/* -------- Right: sale form -------- */}
      <div className="space-y-6 xl:col-span-2">
        {result ? (
          <Section title="Sale recorded successfully" actions={<Badge variant="success"><CheckCircle2 className="w-3 h-3" /> {result.status || "completed"}</Badge>}>
            <div className="space-y-2 text-sm" data-testid="mc-sale-success">
              <div><span className="text-muted-foreground">Transaction:</span> <span className="font-mono font-semibold">{result.invoice_number}</span></div>
              {result.poultry_batch_number && (
                <div data-testid="mc-success-batch"><span className="text-muted-foreground">Batch:</span> <span className="font-semibold">{result.poultry_batch_number}</span></div>
              )}
              <div><span className="text-muted-foreground">Quantity:</span> {result.quantity} birds</div>
              <div><span className="text-muted-foreground">Sale total:</span> <span className="font-semibold">{fmt(result.total, result.transaction_currency)}</span></div>
              <div><span className="text-muted-foreground">Payment received:</span> {(result.payments || []).map(p => `${fmt(p.amount, p.currency)}`).join(" + ") || "—"}</div>
              <div><span className="text-muted-foreground">Balance:</span> <span className={result.balance ? "text-destructive font-semibold" : "text-success font-semibold"}>{fmt(result.balance, result.transaction_currency)}</span></div>
              {result.idempotent_hit && <div className="text-xs text-warning italic">This was an idempotent replay — no duplicate sale was created.</div>}
              {result.opening_batch_balance != null && result.closing_batch_balance != null && (
                <div className="mt-4 rounded-xl border border-border p-3 bg-muted/20" data-testid="mc-success-reconciliation">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Poultry reconciliation — {result.poultry_batch_number || "batch"}</div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><div className="text-[10px] text-muted-foreground">Opening</div><div className="font-heading font-bold" data-testid="mc-open">{result.opening_batch_balance} birds</div></div>
                    <div><div className="text-[10px] text-muted-foreground">Sold</div><div className="font-heading font-bold text-accent">−{result.quantity}</div></div>
                    <div><div className="text-[10px] text-muted-foreground">Closing</div><div className="font-heading font-bold text-success" data-testid="mc-close">{result.closing_batch_balance} birds</div></div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={resetForNext} data-testid="mc-record-another">Record another sale</Button>
            </div>
          </Section>
        ) : (
          <Section title="New multi-currency poultry sale" description="Poultry360 remains authoritative for bird quantity. Sales & Cash is authoritative for money.">
            <div className="mb-3 p-3 rounded-xl bg-primary/8 border border-primary/15 text-sm flex items-center justify-between" data-testid="mc-selling-basis">
              <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Selling basis</span>
              <span className="font-semibold">
                {currency} — {unitPrice ? fmt(unitPrice, currency) : "—"}/bird
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Batch">
                <Select value={batchId} onChange={e => setBatchId(e.target.value)} data-testid="mc-batch-select">
                  <option value="">Select a batch…</option>
                  {batches.map(b => (
                    <option key={b.id} value={b.id}>{b.batch_number} — {b.current_birds} birds available</option>
                  ))}
                </Select>
              </Field>
              <Field label="Available birds">
                <div className="h-10 flex items-center px-3 rounded-lg bg-muted/40 border border-border text-sm font-semibold" data-testid="mc-available">
                  {selectedBatch ? `${available} birds` : "—"}
                </div>
              </Field>
              <Field label="Sale date (defaults to today; pick an earlier date for historical entry)">
                <Input type="date" value={saleDate} max={new Date().toISOString().slice(0, 10)}
                  onChange={e => setSaleDate(e.target.value)} data-testid="mc-sale-date" />
                {saleDate && saleDate < new Date().toISOString().slice(0, 10) && (
                  <div className="text-xs text-amber-700 mt-1 flex items-center gap-1" data-testid="mc-historical-note">
                    <AlertTriangle className="w-3 h-3" /> Historical entry — captured today with business date {saleDate}.
                  </div>
                )}
              </Field>
              <Field label="Quantity">
                <Input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} data-testid="mc-qty" />
                {qtyExceedsAvail && (
                  <div className="text-xs text-destructive mt-1 flex items-center gap-1" data-testid="mc-avail-warn">
                    <AlertTriangle className="w-3 h-3" /> Only {available} birds available in this batch.
                  </div>
                )}
              </Field>
              <Field label="Selling currency">
                <div className="flex gap-2" data-testid="mc-currency-toggle">
                  {["ZAR", "USD"].map(c => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`mc-currency-${c}`}
                      onClick={() => setCurrency(c)}
                      className={`flex-1 h-10 rounded-lg text-sm font-semibold border transition ${currency === c ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted/40"}`}
                    >
                      {c === "ZAR" ? `ZAR — ${priceFor("ZAR") ? fmt(priceFor("ZAR"), "ZAR") : "R—"} / bird` : `USD — ${priceFor("USD") ? fmt(priceFor("USD"), "USD") : "$—"} / bird`}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Customer (optional)">
                <Input value={customer} onChange={e => setCustomer(e.target.value)} data-testid="mc-customer" placeholder="Walk-in / name" />
              </Field>
              <Field label="Notes (optional)">
                <Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="mc-notes" />
              </Field>
            </div>

            {/* Live sale total */}
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/15" data-testid="mc-sale-total-box">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Sale total</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-bold tracking-tight" data-testid="mc-sale-total">{fmt(total, currency)}</span>
                <span className="text-xs text-muted-foreground">= {qty || 0} × {unitPrice ? fmt(unitPrice, currency) : "—"}</span>
              </div>
            </div>

            {/* Payment received */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">Payment received in</div>
                <div className="flex gap-1.5 rounded-full bg-muted/60 p-1 border border-border" data-testid="mc-tender-toggle">
                  {[
                    { k: "ZAR", label: "ZAR only" },
                    { k: "USD", label: "USD only" },
                    { k: "MIXED", label: "Mixed USD + ZAR" },
                  ].map(o => (
                    <button key={o.k} type="button" data-testid={`mc-tender-${o.k}`}
                      onClick={() => setTenderMode(o.k)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${tenderMode === o.k ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{o.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(tenderMode === "ZAR" || tenderMode === "MIXED") && (
                  <Field label="ZAR received">
                    <Input type="number" min={0} step="0.01" value={zarPaid} onChange={e => setZarPaid(e.target.value)} data-testid="mc-zar-paid" placeholder="R0.00" />
                  </Field>
                )}
                {(tenderMode === "USD" || tenderMode === "MIXED") && (
                  <Field label="USD received">
                    <Input type="number" min={0} step="0.01" value={usdPaid} onChange={e => setUsdPaid(e.target.value)} data-testid="mc-usd-paid" placeholder="$0.00" />
                  </Field>
                )}
              </div>

              {/* Live conversion / settlement summary */}
              {tenderMode === "MIXED" && (
                <div className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-lg p-3 space-y-1" data-testid="mc-conversion">
                  {usdPaidNum > 0 && rateUsdZar && (
                    <div>USD {usdPaidNum.toFixed(2)} × R{rateUsdZar.toFixed(2)} = <span className="font-semibold text-foreground">{fmt(usdPaidNum * rateUsdZar, "ZAR")}</span></div>
                  )}
                  {zarPaidNum > 0 && (<div>ZAR received: <span className="font-semibold text-foreground">{fmt(zarPaidNum, "ZAR")}</span></div>)}
                </div>
              )}

              {/* Change physically returned — MANDATORY when tender > amount due */}
              {settlementEquivalent > total + 0.005 && (
                <div className="mt-2 p-3 rounded-xl border border-warning/40 bg-warning/5" data-testid="mc-change-panel">
                  <div className="text-xs font-semibold text-warning mb-2">
                    Tender exceeds amount due. Record the actual change physically returned to the customer
                    ({tenderMode === "MIXED" ? "you may split across currencies" : `in ${currency}`}):
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Change returned in ZAR">
                      <Input type="number" min={0} step="0.01" value={zarChange} onChange={e => setZarChange(e.target.value)} data-testid="mc-zar-change" placeholder="R0.00" />
                    </Field>
                    <Field label="Change returned in USD">
                      <Input type="number" min={0} step="0.01" value={usdChange} onChange={e => setUsdChange(e.target.value)} data-testid="mc-usd-change" placeholder="$0.00" />
                    </Field>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2 italic">
                    The reporting equivalent is never assumed to be physical change. Record what actually left the till.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="rounded-xl border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Amount due</div>
                  <div className="text-lg font-heading font-bold" data-testid="mc-due">{fmt(total, currency)}</div>
                </div>
                <div className="rounded-xl border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Settlement equivalent</div>
                  <div className="text-lg font-heading font-bold" data-testid="mc-settlement">{fmt(settlementEquivalent, currency)}</div>
                </div>
                <div className="rounded-xl border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance</div>
                  <div className={`text-lg font-heading font-bold ${Math.abs(balance) < 0.005 ? "text-success" : (balance > 0 ? "text-destructive" : "text-warning")}`} data-testid="mc-balance">
                    {fmt(Math.abs(balance), currency)}{balance > 0.005 ? " outstanding" : balance < -0.005 ? " over" : ""}
                  </div>
                </div>
              </div>

              {/* Net physical cash movement — mandatory transparency for mixed / change scenarios */}
              {(payments.length > 1 || changeReturned.length > 0) && (
                <div className="mt-2 text-xs bg-muted/30 border border-border rounded-lg p-3" data-testid="mc-net-movement">
                  <div className="font-semibold text-foreground mb-1">Net physical cash movement</div>
                  <div>ZAR: {fmt(zarPaidNum - zarChangeNum, "ZAR")} <span className="text-muted-foreground">({fmt(zarPaidNum, "ZAR")} tendered − {fmt(zarChangeNum, "ZAR")} change)</span></div>
                  <div>USD: {fmt(usdPaidNum - usdChangeNum, "USD")} <span className="text-muted-foreground">({fmt(usdPaidNum, "USD")} tendered − {fmt(usdChangeNum, "USD")} change)</span></div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-2 items-center">
              <Button onClick={complete} disabled={!canSubmit} data-testid="mc-complete-sale" className="min-w-[180px]">
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Recording…</>) : (<><Sparkles className="w-4 h-4" /> Complete sale</>)}
              </Button>
              {!canWrite && <span className="text-xs text-muted-foreground">You do not have permission to record sales.</span>}
              {!rateUsdZar && <span className="text-xs text-destructive">No active USD⇄ZAR rate configured.</span>}
              {batches.length === 0 && (
                <EmptyState icon={Bird} title="No batches with available birds" description="Poultry360 shows no active batch with saleable birds." />
              )}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
